import { useEffect, useState } from 'react'
import { sendGet, sendPost, sendPut } from 'hooks/use-fetch'
import { type postError, type Restraint } from 'core/types'
import { TextFieldWithLabel } from 'Components/TextField'
import { TextAreaWithLabel } from 'Components/TextArea'
import { Modal } from 'Components/Modal'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { TimePickerWithLabel } from 'Components/TimePicker'
import { toast } from 'react-toastify'

interface RestraintEditProps {
  editID: number | null // pass null to create a new restraint
  enrollmentID: number | null
  onClose: (update: boolean) => void
}

export function RestraintEdit (props: RestraintEditProps): JSX.Element {
  const initialRestraint: Restraint = {
    id: null,
    enrollmentID: props.enrollmentID ?? 0,
    userID: 0,
    whenEntered: new Date(),
    studentHasMedicalCondition: false,
    medicalConditionDescription: null,
    eventDate: new Date(),
    whatHappenedBefore: null,
    behaviorDescription: null,
    behaviorDurationBeforeIntervention: null,
    strategiesToStopBehavior: null,
    preventionStrategiesDuration: null,
    timeInterventionStarted: null,
    whoInitiatedIntervention: null,
    staffInvolved: null,
    interventionLocation: null,
    restraintDescription: null,
    minutesRestrained: 0,
    extendedTimeExplanation: null,
    studentResponse: null,
    wereInjuriesSustained: false,
    injuryDescription: null,
    followUp: null,
    dateParentContacted: null,
    timeParentContacted: null,
    reportGivenToParent: false,
    writtenReportEffort: null
  }

  const [restraint, setRestraint] = useState<Restraint>(initialRestraint)

  useEffect(() => {
    if (props.editID != null) {
      const fetchData = async (): Promise<void> => {
        const { response: providers } = await sendGet('/RestraintEvent/Get?id=' + String(props.editID), {})
        setRestraint(providers)
      }

      void fetchData()
    }
  }, [])

  const handleChange = (e: any): void => {
    setRestraint({ ...restraint, [e.target.name]: e.target.value })
  }

  const valueChanged = (field: string, value: any): void => {
    setRestraint({ ...restraint, [field]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    let callback: { response: any, success: boolean }
    if (props.editID != null) {
      callback = await sendPut('/RestraintEvent/Update', restraint)
    } else {
      callback = await sendPost('/RestraintEvent/Insert', restraint)
    }

    const errors = callback.response.errors as postError[]
    if (errors.length > 0) {
      errors.forEach(e => {
        toast.error(e.error)
      })
    } else {
      toast.success('Record Saved')
      props.onClose(true)
    }
  }

  const handleClose = (): void => {
    props.onClose(false)
  }

  const content = <>
    <DatePickerWithLabel
      label='Date the problem behavior occurred'
      name='eventDate'
      data-testid="eventDate"
      required={true}
      onChange={(value) => { valueChanged('eventDate', value) }}
      value={restraint.eventDate}
    />
    <CheckBoxWithLabel label='Does the student have a known medical condition?'
      name='studentHasMedicalCondition'
      edge='start'
      value={restraint.studentHasMedicalCondition}
      setValue={(val) => { valueChanged('studentHasMedicalCondition', val) }}
    />
    <TextFieldWithLabel label='If yes, provide a brief description'
      data-testid='medicalConditionDescription'
      name='medicalConditionDescription'
      maxLength={500}
      onChange={handleChange}
      value={restraint.medicalConditionDescription}
    />
    <TextAreaWithLabel
      label='What happened before the behavior occurred? Describe the antecedents or triggers'
      name='whatHappenedBefore'
      onChange={handleChange}
      value={restraint.whatHappenedBefore}
      required
    />
    <TextAreaWithLabel
      label='Describe the behavior. Include the frequency and intensity. Explain how it posed an imminent risk to the safety of the individual student or others'
      name='behaviorDescription'
      onChange={handleChange}
      value={restraint.behaviorDescription}
      required
    />
    <TextAreaWithLabel
      label='How long did the problem behavior last before the intervention?'
      name='behaviorDurationBeforeIntervention'
      onChange={handleChange}
      value={restraint.behaviorDurationBeforeIntervention}
      required
    />
    <TextAreaWithLabel
      label='Describe the strategies or interventions attempted to stop the behavior prior to the use of seclusion or restraint.'
      name='strategiesToStopBehavior'
      onChange={handleChange}
      value={restraint.strategiesToStopBehavior}
      required
    />
    <TextAreaWithLabel
      label='For how long were the prevention strategies or interventions attempted prior to the use of seclusion or restraint?'
      name='preventionStrategiesDuration'
      onChange={handleChange}
      value={restraint.preventionStrategiesDuration}
      required
    />
    <TextFieldWithLabel label='Time the intervention started'
      data-testid='timeInterventionStarted'
      name='timeInterventionStarted'
      maxLength={50}
      onChange={handleChange}
      value={restraint.timeInterventionStarted}
      required
    />
    <TextFieldWithLabel label='Staff member who initiated the intervention'
      data-testid='whoInitiatedIntervention'
      name='whoInitiatedIntervention'
      maxLength={500}
      onChange={handleChange}
      value={restraint.whoInitiatedIntervention}
      required
    />
    <TextAreaWithLabel
      label='Staff involved (mark an asterisk * next to those who previously received comprehensive training on the use of emergency physical restraint)'
      name='staffInvolved'
      onChange={handleChange}
      value={restraint.staffInvolved}
      required
    />
    <TextAreaWithLabel
      label='Location(s) of the intervention'
      name='interventionLocation'
      onChange={handleChange}
      value={restraint.interventionLocation}
      required
    />
    <TextAreaWithLabel
      label='Describe the restraint that was used'
      name='restraintDescription'
      onChange={handleChange}
      value={restraint.restraintDescription}
      required
    />
    <TextFieldWithLabel label='Length of time restraint was used in minutes. (Allowable time for emergency physical restraint without extensions is 10)'
      data-testid='minutesRestrained'
      name='minutesRestrained'
      type='number'
      onChange={handleChange}
      value={restraint.minutesRestrained}
      required
    />
    <TextAreaWithLabel
      label='If extended time was used, explain the reason why. Include a description of the additional support used during the extended time.'
      name='extendedTimeExplanation'
      onChange={handleChange}
      value={restraint.extendedTimeExplanation}
    />
    <TextAreaWithLabel
      label='Describe the student response to the intervention'
      name='studentResponse'
      onChange={handleChange}
      value={restraint.studentResponse}
      required
    />
    <CheckBoxWithLabel label='Were any injuries sustained during the intervention?'
      name='wereInjuriesSustained'
      edge='start'
      value={restraint.wereInjuriesSustained}
      setValue={(val) => { valueChanged('wereInjuriesSustained', val) }}
    />
    <TextAreaWithLabel
      label='If yes, identify who was injured and briefly describe the injury'
      name='injuryDescription'
      onChange={handleChange}
      value={restraint.injuryDescription}
    />
    <TextAreaWithLabel
      label='Describe the follow-up with the student after the intervention. Include when and where the follow-up occurred'
      name='followUp'
      onChange={handleChange}
      value={restraint.followUp}
      required
    />
    <DatePickerWithLabel
      label='Date the parent was contacted'
      name='dateParentContacted'
      data-testid="dateParentContacted"
      required={true}
      onChange={(value) => { valueChanged('dateParentContacted', value) }}
      value={restraint.dateParentContacted}
    />
    <TimePickerWithLabel
      label='Time the parent was contacted'
      name='timeParentContacted'
      required={true}
      onChange={(d) => { valueChanged('timeParentContacted', d) }}
      onAccept={(d) => { valueChanged('timeParentContacted', d) }}
      value={restraint.timeParentContacted}
    />
    <CheckBoxWithLabel label='Was a written report given to the parent/guardian?'
      name='reportGivenToParent'
      edge='start'
      value={restraint.reportGivenToParent}
      setValue={(val) => { valueChanged('reportGivenToParent', val) }}
    />
    <TextAreaWithLabel
      label='If the parent did not receive a written report, document the reasonable effort made to provide one'
      name='writtenReportEffort'
      onChange={handleChange}
      value={restraint.writtenReportEffort}
    />
  </>

  return <Modal
    data-testid="modal"
    open={true}
    title={(props.editID === null ? 'New Restraint Event' : 'Edit Restraint Event')}
    confirmationContent={content}
    onClose={handleClose}
    onConfirm={handleSubmit}
  />
}
